import React, { useState } from "react";
import {
  Radio,
  Checkbox,
  Switch,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tooltip,
} from "antd";
import {
  UpOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const Header = ({
  chartLevel,
  setChartLevel,
  selectedFuelTypes,
  setSelectedFuelTypes,
  availableFuelTypes,
  selectedVariable,
  setSelectedVariable,
  showOnlyZeroAvail,
  setShowOnlyZeroAvail,
  viewMode,
  setViewMode,
  autoRefresh,
  setAutoRefresh,
  sharedTooltip,
  setSharedTooltip,
  runDateTime,
  onRefresh,
  loading,
  isMobile,
}) => {
  const [headerCollapsed, setHeaderCollapsed] = useState(false);

  // Convert UTC to AEST (UTC+10, no daylight savings)
  const formatToAEST = (utcDateString) => {
    if (!utcDateString) return "Loading...";
    try {
      return (
        new Date(utcDateString).toLocaleString("en-AU", {
          timeZone: "Australia/Brisbane",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }) + " AEST"
      );
    } catch (e) {
      return utcDateString;
    }
  };

  return (
      <Card style={{ marginBottom: 16, borderTop: "3px solid #143660" }}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 8 : 0,
            marginBottom: isMobile || headerCollapsed ? 0 : 16,
          }}
        >
          <Space size="middle" wrap direction={isMobile ? "vertical" : "horizontal"}>
            <Title level={3} style={{ margin: 0, color: "#143660" }}>
              Maximum Availability Visualization (V2)
            </Title>
            <div>
              <Text strong style={{ color: "#13a89e" }}>
                AEMO Run DateTime:{" "}
              </Text>
              <Text type="secondary">
                {formatToAEST(runDateTime)}
              </Text>
            </div>
          </Space>
          <Space>
            <Button
              style={{
                backgroundColor: "#13a89e",
                borderColor: "#13a89e",
                color: "white",
              }}
              onClick={onRefresh}
              loading={loading}
            >
              Fetch Latest Data
            </Button>
            {!isMobile && (
              <Button
                type="text"
                icon={headerCollapsed ? <DownOutlined /> : <UpOutlined />}
                onClick={() => setHeaderCollapsed(!headerCollapsed)}
                style={{ color: "#7396bf" }}
              >
                {headerCollapsed ? "Expand" : "Collapse"}
              </Button>
            )}
          </Space>
        </div>

        {!isMobile && !headerCollapsed && (
          <Row gutter={[16, 16]}>
            <Col span={24} style={{ paddingTop: 0 }}>
              {/* Spacer */}
            </Col>

            {/* LEFT COLUMN */}
            <Col xs={24} md={12}>
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="large"
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text strong>Chart Level:</Text>
                  <Radio.Group
                    value={chartLevel}
                    onChange={(e) => setChartLevel(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="system">System</Radio.Button>
                    <Radio.Button value="region">Region</Radio.Button>
                    <Radio.Button value="fuel_type">Fuel Type</Radio.Button>
                    <Radio.Button value="portfolio">Portfolio</Radio.Button>
                    <Radio.Button value="station">Station</Radio.Button>
                    <Radio.Button value="duid">DUID</Radio.Button>
                  </Radio.Group>
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <Text strong>Select Variable:</Text>
                    <Tooltip title="data from MMS.STPASA_DUIDAVAILABILITY table">
                      <InfoCircleOutlined
                        style={{ color: "#13a89e", cursor: "help" }}
                      />
                    </Tooltip>
                  </Space>
                  <Radio.Group
                    value={selectedVariable}
                    onChange={(e) => setSelectedVariable(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="generation_max_availability">
                      Generation Max Availability
                    </Radio.Button>
                    <Radio.Button value="generation_pasa_availability">
                      Generation PASA Availability
                    </Radio.Button>
                  </Radio.Group>
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text strong>View Mode:</Text>
                  <Radio.Group
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="chart">Charts</Radio.Button>
                    <Radio.Button value="table">Table</Radio.Button>
                    <Radio.Button value="both">Both</Radio.Button>
                  </Radio.Group>
                </Space>
              </Space>
            </Col>

            {/* MIDDLE COLUMN */}
            <Col xs={24} md={12}>
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="large"
              >
                {availableFuelTypes && availableFuelTypes.length > 0 && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text strong>Fuel Types:</Text>
                    <Checkbox.Group
                      options={availableFuelTypes.map((ft) => ({
                        label: ft,
                        value: ft,
                      }))}
                      value={selectedFuelTypes}
                      onChange={setSelectedFuelTypes}
                    />
                  </Space>
                )}

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <Text strong>Show only DUIDs with zero availability:</Text>
                    <Tooltip title="Only DUIDs with at least one zero value are shown">
                      <InfoCircleOutlined
                        style={{ color: "#13a89e", cursor: "help" }}
                      />
                    </Tooltip>
                    <Switch
                      checked={showOnlyZeroAvail}
                      onChange={setShowOnlyZeroAvail}
                    />
                  </Space>
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <Text strong>Show all tooltip values:</Text>
                    <Switch
                      checked={sharedTooltip}
                      onChange={setSharedTooltip}
                    />
                  </Space>
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <Text strong>Auto-refresh (30s):</Text>
                    <Switch checked={autoRefresh} onChange={setAutoRefresh} />
                  </Space>
                </Space>
              </Space>
            </Col>
          </Row>
        )}
      </Card>
  );
};

export default Header;
